'use strict';
// Trusted geometry compiler: input is bounded data, never source or SVG markup.
const TONES = ['ink','muted','accent','blue','teal','amber','rose'];
const AXES = ['x','y','z'];
const TYPES = ['box','panel','cylinder','cone','ring','gear','tube','sphere'];
const rad = degrees => degrees * Math.PI / 180;
const add = (a,b) => a.map((v,i)=>v+b[i]);
const sub = (a,b) => a.map((v,i)=>v-b[i]);
const mul = (a,k) => a.map(v=>v*k);
const dot = (a,b) => a.reduce((sum,v,i)=>sum+v*b[i],0);
const cross = (a,b) => [a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
const length = a => Math.hypot(...a);
const unit = a => mul(a,1/(length(a)||1));
const clamp = (v,a,b) => Math.max(a,Math.min(b,v));
function rotate(point,axis,degrees) {
  const a=AXES.indexOf(axis),b=(a+1)%3,c=(a+2)%3,t=rad(degrees),out=[...point];
  out[b]=point[b]*Math.cos(t)-point[c]*Math.sin(t);
  out[c]=point[b]*Math.sin(t)+point[c]*Math.cos(t);
  return out;
}
function rotation(point,angles=[0,0,0]) { return AXES.reduce((p,axis,i)=>rotate(p,axis,angles[i]),point); }
function axisPoint(p,axis='z') { return axis==='x'?[p[2],p[0],p[1]]:axis==='y'?[p[1],p[2],p[0]]:p; }
function ringPoints(radius,z,count) { return Array.from({length:count},(_,i)=>[radius*Math.cos(i/count*Math.PI*2),radius*Math.sin(i/count*Math.PI*2),z]); }
function extrude(outline,height) {
  const bottom=outline.map(([x,y])=>[x,y,-height/2]),top=outline.map(([x,y])=>[x,y,height/2]);
  return [bottom.slice().reverse(),top,...outline.map((_,i)=>{const j=(i+1)%outline.length;return [bottom[i],bottom[j],top[j],top[i]];})];
}
function cylinder(radius,height,count=12,topRadius=radius) {
  const bottom=ringPoints(radius,-height/2,count),top=ringPoints(topRadius,height/2,count);
  return [bottom.slice().reverse(),top,...bottom.map((p,i)=>{const j=(i+1)%count;return [p,bottom[j],top[j],top[i]];})];
}
function sphere(radius,count=12) {
  const rows=count/2,faces=[];
  const point=(i,j)=>{const theta=i/count*Math.PI*2,phi=j/rows*Math.PI;return [radius*Math.sin(phi)*Math.cos(theta),radius*Math.sin(phi)*Math.sin(theta),radius*Math.cos(phi)];};
  for(let j=0;j<rows;j++) for(let i=0;i<count;i++) {
    const face=[point(i,j),point(i,j+1),point(i+1,j+1),point(i+1,j)];
    faces.push(j===0?face.slice(0,3):j===rows-1?[face[0],face[1],face[3]]:face);
  }
  return faces;
}
function mesh(solid) {
  const count=solid.segments||12;let faces;
  if(solid.type==='box'||solid.type==='panel') {
    const [x,y,z]=solid.size.map(v=>v/2);
    faces=extrude([[-x,-y],[x,-y],[x,y],[-x,y]],z*2);
  } else if(solid.type==='sphere') faces=sphere(solid.radius,count);
  else if(solid.type==='ring') {
    const outer=[ringPoints(solid.radius,-solid.height/2,count),ringPoints(solid.radius,solid.height/2,count)];
    const inner=[ringPoints(solid.innerRadius,-solid.height/2,count),ringPoints(solid.innerRadius,solid.height/2,count)];
    faces=[];
    for(let i=0;i<count;i++) { const j=(i+1)%count;
      faces.push([outer[0][i],outer[0][j],outer[1][j],outer[1][i]],
        [inner[0][j],inner[0][i],inner[1][i],inner[1][j]],
        [outer[1][i],outer[1][j],inner[1][j],inner[1][i]],
        [outer[0][j],outer[0][i],inner[0][i],inner[0][j]]);
    }
  } else if(solid.type==='gear') {
    const points=Array.from({length:solid.teeth*4},(_,i)=>{const angle=i/(solid.teeth*4)*Math.PI*2,r=solid.radius*(i%4===0||i%4===3?.82:1);return [r*Math.cos(angle),r*Math.sin(angle)];});
    // Concave caps are emitted as a fan, avoiding invalid concave SVG depth faces.
    faces=extrude(points,solid.height).slice(2);
    for(let i=0;i<points.length;i++) { const a=points[i],b=points[(i+1)%points.length];
      faces.push([[0,0,solid.height/2],[...a,solid.height/2],[...b,solid.height/2]],
        [[0,0,-solid.height/2],[...b,-solid.height/2],[...a,-solid.height/2]]);
    }
  } else if(solid.type==='tube') {
    faces=[];
    for(let i=1;i<solid.points.length;i++) {
      const start=solid.points[i-1],end=solid.points[i],direction=unit(sub(end,start));
      const u=unit(cross(direction,Math.abs(direction[2])<.9?[0,0,1]:[0,1,0])),v=cross(direction,u),center=mul(add(start,end),.5);
      faces.push(...cylinder(solid.radius,length(sub(end,start)),count).map(face=>face.map(([x,y,z])=>add(center,add(mul(u,x),add(mul(v,y),mul(direction,z)))))));
    }
    // Round elbows close bends without requiring model-authored joints.
    for(const p of solid.points.slice(1,-1)) faces.push(...sphere(solid.radius,8).map(face=>face.map(v=>add(v,p))));
  } else faces=cylinder(solid.radius,solid.height,count,solid.type==='cone'?solid.topRadius:solid.radius);
  return faces.map(face=>{const result=face.map(p=>add(rotation(axisPoint(p,solid.axis),solid.rotation),solid.position));result.smooth=!['box','panel'].includes(solid.type);return result;});
}
function transformPart(point,part,phase,separation) {
  let out=point;
  if(part.motion) {
    const {type,axis,range,pivot=[0,0,0]}=part.motion,amount=range[0]+(range[1]-range[0])*phase;
    out=type==='rotate'?add(rotate(sub(out,pivot),axis,amount),pivot):add(out,AXES.map(a=>a===axis?amount:0));
  }
  return add(out,mul(part.offset3d||[0,0,0],separation));
}
function validateScene(node,state,fail,path) {
  const fields=(obj,allowed,required,p)=>{
    if(!obj||typeof obj!=='object'||Array.isArray(obj)) fail('diagram_scene',p);
    if(Object.keys(obj).some(k=>!allowed.includes(k))||required.some(k=>obj[k]===undefined)) fail('diagram_scene_fields',p);
  };
  const vector=(v,p,limit=1000)=>{if(!Array.isArray(v)||v.length!==3||!v.every(n=>Number.isFinite(n)&&Math.abs(n)<=limit))fail('diagram_scene_vector',p);};
  const positive=(v,p)=>{if(!Number.isFinite(v)||v<=0||v>500)fail('diagram_scene_size',p);};
  fields(node.scene,['azimuth','elevation','effects'],[],path+'/scene');
  for(const [key,low,high] of [['azimuth',-180,180],['elevation',5,80]]) if(node.scene[key]!==undefined&&(!Number.isFinite(node.scene[key])||node.scene[key]<low||node.scene[key]>high))fail('diagram_camera',path+'/scene/'+key);
  if(node.phaseBind!==undefined) {
    const def=state[node.phaseBind];
    if(!def||def.type!=='number'||def.min!==0||def.max!==1||node.phaseBind===node.explodeBind)fail('diagram_phase',path);
  }
  if(node.phaseLabel!==undefined&&(typeof node.phaseLabel!=='string'||!node.phaseLabel.trim()||node.phaseLabel.length>80||!node.phaseBind))fail('diagram_phase',path);
  let solids=0,cost=0,moving=0;
  const ids=new Set(),labels=new Set();
  if(!Array.isArray(node.parts)||node.parts.length<2||node.parts.length>10)fail('diagram_parts',path);
  for(const [i,part] of node.parts.entries()) {
    const p=path+'/parts/'+i;
    fields(part,['id','label','description','solids','offset3d','motion'],['id','label','description','solids'],p);
    if(typeof part.id!=='string'||!/^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/.test(part.id)||['constructor','prototype','__proto__'].includes(part.id)||ids.has(part.id)
      ||typeof part.label!=='string'||!part.label.trim()||part.label.length>80||part.label==='All'||labels.has(part.label)
      ||typeof part.description!=='string'||!part.description.trim()||part.description.length>1200)fail('diagram_part',p);
    ids.add(part.id);labels.add(part.label);
    if(part.offset3d!==undefined)vector(part.offset3d,p,300);
    if(part.motion!==undefined) {
      fields(part.motion,['type','axis','range','pivot'],['type','axis','range'],p+'/motion');
      if(!node.phaseBind||!['rotate','translate'].includes(part.motion.type)||!AXES.includes(part.motion.axis)||!Array.isArray(part.motion.range)||part.motion.range.length!==2
        ||!part.motion.range.every(n=>Number.isFinite(n)&&Math.abs(n)<=(part.motion.type==='rotate'?720:300))||part.motion.range[0]===part.motion.range[1])fail('diagram_motion',p);
      if(part.motion.pivot!==undefined) { if(part.motion.type!=='rotate')fail('diagram_motion',p);vector(part.motion.pivot,p,1000); }
      moving++;
    }
    if(!Array.isArray(part.solids)||part.solids.length<1||part.solids.length>24)fail('diagram_solids',p);
    for(const [j,s] of part.solids.entries()) {
      const sp=p+'/solids/'+j;
      const geometry={box:['size'],panel:['size'],cylinder:['radius','height'],cone:['radius','topRadius','height'],ring:['radius','innerRadius','height'],gear:['radius','height','teeth'],tube:['radius','points'],sphere:['radius']}[s?.type];
      if(!TYPES.includes(s?.type))fail('diagram_solid',sp);
      fields(s,['type','position','tone','rotation',...(!['box','panel','sphere','tube'].includes(s.type)?['axis']:[]),...(!['box','panel','gear'].includes(s.type)?['segments']:[]),...geometry],['type','position',...geometry],sp);
      vector(s.position,sp);if(s.rotation!==undefined)vector(s.rotation,sp,360);
      if(s.tone!==undefined&&!TONES.includes(s.tone))fail('diagram_style',sp);
      if(s.axis!==undefined&&!AXES.includes(s.axis))fail('diagram_axis',sp);
      if(s.segments!==undefined&&(!Number.isInteger(s.segments)||s.segments<8||s.segments>16||s.segments%2))fail('diagram_segments',sp);
      if(s.size!==undefined) { vector(s.size,sp,500);s.size.forEach(n=>positive(n,sp)); }
      for(const k of ['radius','height','innerRadius'])if(s[k]!==undefined)positive(s[k],sp);
      if(s.type==='cone'&&(!Number.isFinite(s.topRadius)||s.topRadius<0||s.topRadius>s.radius))fail('diagram_scene_size',sp);
      if(s.type==='ring'&&s.innerRadius>=s.radius)fail('diagram_scene_size',sp);
      if(s.type==='gear'&&(!Number.isInteger(s.teeth)||s.teeth<6||s.teeth>16))fail('diagram_teeth',sp);
      if(s.type==='tube') {
        if(!Array.isArray(s.points)||s.points.length<2||s.points.length>8)fail('diagram_tube',sp);
        s.points.forEach(v=>vector(v,sp,500));
        for(let k=1;k<s.points.length;k++)if(length(sub(s.points[k],s.points[k-1]))<.01)fail('diagram_tube',sp);
      }
      // Bound compiled work before allocating any geometry on either server or client.
      const n=s.segments||12;
      cost+=s.type==='sphere'?n*n/2:s.type==='gear'?s.teeth*12:s.type==='ring'?n*4:s.type==='tube'?(s.points.length-1)*(n+2)+(s.points.length-2)*32:s.type==='box'||s.type==='panel'?6:n+2;
      if(++solids>64||cost>1800)fail('diagram_scene_limit',sp);
    }
  }
  if(node.phaseBind&&!moving)fail('diagram_motion',path);
  if(node.explodeBind&&!node.parts.some(p=>p.offset3d?.some(n=>n!==0)))fail('diagram_explosion',path);
  const options=state[node.bind]?.options;
  if(!options||options.length!==node.parts.length+1||options[0]!=='All'||node.parts.some((p,i)=>options[i+1]!==p.label))fail('diagram_choices',path);
  if(node.scene.effects!==undefined) {
    if(!Array.isArray(node.scene.effects)||node.scene.effects.length>4)fail('diagram_effects',path);
    for(const effect of node.scene.effects) {
      fields(effect,['type','partId','position','direction','size','tone','activeFrom'],['type','partId','position','size'],path+'/scene/effects');
      if(!['glow','flow','flame','steam','water'].includes(effect.type)||!ids.has(effect.partId)||effect.tone!==undefined&&!TONES.includes(effect.tone))fail('diagram_effect',path);
      vector(effect.position,path);positive(effect.size,path);
      if(effect.direction!==undefined) {vector(effect.direction,path,1);if(length(effect.direction)<.01)fail('diagram_effect',path);}
      if(effect.activeFrom!==undefined&&(!node.phaseBind||!Number.isFinite(effect.activeFrom)||effect.activeFrom<0||effect.activeFrom>1))fail('diagram_effect',path);
    }
  }
  return {solids,faces:cost};
}
function compileScene(node) {
  const parts=node.parts.map(part=>({...part,meshes:part.solids.map(s=>({tone:s.tone||'ink',faces:mesh(s)}))}));
  // Conservative bounds cover every motion frame and both separation endpoints.
  // A rotating part sweeps a cylinder about its real pivot, not a sampled pose.
  const bounds=[],partBounds=[];
  for(const part of parts) {
    const vertices=part.meshes.flatMap(m=>m.faces.flat());
    for(const e of (node.scene.effects||[]).filter(e=>e.partId===part.id)) {
      const end=add(e.position,mul(unit(e.direction||[0,0,1]),e.size));
      for(const center of [e.position,end])for(let i=0;i<8;i++)vertices.push(center.map((v,a)=>v+((i&(1<<a))?1:-1)*e.size));
    }
    const points=[...vertices];
    if(part.motion?.type==='rotate') {
      const axis=AXES.indexOf(part.motion.axis),pivot=part.motion.pivot||[0,0,0],others=[0,1,2].filter(i=>i!==axis);
      const r=Math.max(...vertices.map(v=>Math.hypot(...others.map(i=>v[i]-pivot[i]))));
      const low=Math.min(...vertices.map(v=>v[axis])),high=Math.max(...vertices.map(v=>v[axis]));
      points.length=0;
      for(const a of [low,high])for(const b of [-r,r])for(const c of [-r,r]) {const p=[...pivot];p[axis]=a;p[others[0]]+=b;p[others[1]]+=c;points.push(p);}
    } else if(part.motion) {
      points.length=0;
      for(const t of [0,1]) points.push(...vertices.map(v=>transformPart(v,{...part,offset3d:[0,0,0]},t,0)));
    }
    const low=[0,1,2].map(i=>Math.min(...points.map(v=>v[i]))),high=[0,1,2].map(i=>Math.max(...points.map(v=>v[i])));
    const corners=Array.from({length:8},(_,i)=>[0,1,2].map(a=>(i&(1<<a))?high[a]:low[a]));
    partBounds.push({corners,offset:part.offset3d||[0,0,0]});
    for(const t of [0,1])bounds.push(...corners.map(v=>add(v,mul(part.offset3d||[0,0,0],t))));
  }
  const low=[0,1,2].map(i=>Math.min(...bounds.map(v=>v[i]))),high=[0,1,2].map(i=>Math.max(...bounds.map(v=>v[i])));
  return {parts,partBounds,corners:Array.from({length:8},(_,i)=>[0,1,2].map(a=>(i&(1<<a))?high[a]:low[a]))};
}
function cameraFor(compiled,azimuth=45,elevation=25,zoom=1,separation=0) {
  const a=rad(azimuth),e=rad(elevation),right=[Math.cos(a),-Math.sin(a),0],down=[Math.sin(a)*Math.sin(e),Math.cos(a)*Math.sin(e),-Math.cos(e)],viewer=[Math.sin(a)*Math.cos(e),Math.cos(a)*Math.cos(e),Math.sin(e)];
  // Fit the current disassembly pose while reserving the entire motion sweep.
  // This keeps playback steady without shrinking the assembled view for a
  // future explosion; easing separation also eases its automatic camera fit.
  const corners=compiled.partBounds.flatMap(p=>p.corners.map(v=>add(v,mul(p.offset,separation))));
  const flat=corners.map(p=>[dot(p,right),dot(p,down)]),lo=[0,1].map(i=>Math.min(...flat.map(p=>p[i]))),hi=[0,1].map(i=>Math.max(...flat.map(p=>p[i])));
  const scale=Math.min(780/Math.max(1,hi[0]-lo[0]),410/Math.max(1,hi[1]-lo[1]));
  return {viewer,scale:scale*clamp(zoom,.7,2),project:p=>[500+(dot(p,right)-(lo[0]+hi[0])/2)*scale*clamp(zoom,.7,2),285+(dot(p,down)-(lo[1]+hi[1])/2)*scale*clamp(zoom,.7,2)]};
}
function renderScene(compiled,node,{phase=0,separation=0,azimuth=node.scene.azimuth??45,elevation=node.scene.elevation??25,zoom=1,selected}={}) {
  const camera=cameraFor(compiled,azimuth,elevation,zoom,separation),faces=[],anchors={},effects=[];
  for(const part of compiled.parts) {
    const world=[];
    for(const [mi,m] of part.meshes.entries())for(const [fi,face] of m.faces.entries()) {
      const points=face.map(p=>transformPart(p,part,phase,separation));world.push(...points);
      const normal=unit(cross(sub(points[1],points[0]),sub(points[2],points[0])));
      if(dot(normal,camera.viewer)<=.00001)continue;
      const brightness=clamp(dot(normal,unit([-.25,.45,1])),0,1);
      faces.push({key:`${part.id}-${mi}-${fi}`,type:'polygon',points:points.map(camera.project),filled:true,tone:m.tone,strokeWidth:.8,smooth:face.smooth,shade:Math.round((1-brightness)*3),partId:part.id,depth:dot(mul(points.reduce(add,[0,0,0]),1/points.length),camera.viewer),opacity:!selected||selected===part.id?1:.18});
    }
    anchors[part.id]=camera.project(mul(world.reduce(add,[0,0,0]),1/world.length));
  }
  faces.sort((a,b)=>a.depth-b.depth);
  for(const [i,e] of (node.scene.effects||[]).entries()) {
    const part=compiled.parts.find(p=>p.id===e.partId);
    if(phase<(e.activeFrom??0))continue;
    const center=transformPart(e.position,part,phase,separation),direction=e.direction||[0,0,1],end=transformPart(add(e.position,mul(unit(direction),e.size)),part,phase,separation);
    effects.push({...e,key:String(i),point:camera.project(center),end:camera.project(end),radius:e.size*camera.scale,opacity:!selected||selected===e.partId?1:.18,phase});
  }
  return {faces,anchors,effects};
}
function layoutSceneCallouts(parts,anchors) {
  // Choose margins by projected position, then preserve vertical order on each
  // side. Array-order labels can send leaders across the whole assembly.
  const ordered=parts.map(part=>({id:part.id,anchor:anchors[part.id]}))
    .sort((a,b)=>a.anchor[0]-b.anchor[0]||a.id.localeCompare(b.id));
  const split=Math.ceil(ordered.length/2),result={};
  for(const [side,items] of [ordered.slice(0,split),ordered.slice(split)].entries()) {
    items.sort((a,b)=>a.anchor[1]-b.anchor[1]||a.id.localeCompare(b.id));
    const ys=items.map(item=>clamp(item.anchor[1]-20,45,495));
    for(let i=1;i<ys.length;i++)ys[i]=Math.max(ys[i],ys[i-1]+54);
    for(let i=ys.length-1;i>=0;i--)ys[i]=Math.min(ys[i],i===ys.length-1?495:ys[i+1]-54);
    items.forEach((item,i)=>{const x=side===0?15:805,y=ys[i];result[item.id]={x,y,leader:[[side===0?195:805,y+20],item.anchor]};});
  }
  return result;
}
function shadedFill(fill,shade=0) {
  if(!/^#[a-f0-9]{6}$/i.test(fill||''))return fill;
  const rgb=[1,3,5].map(i=>parseInt(fill.slice(i,i+2),16));
  // The same four-tone lighting preserves hue and works in light and dark cards.
  const dark=(rgb[0]+rgb[1]+rgb[2])/3<100,target=dark?255:0,amount=dark?[.28,.17,.07,0][shade]:[0,.09,.19,.31][shade];
  return '#'+rgb.map(v=>Math.round(v+(target-v)*amount).toString(16).padStart(2,'0')).join('');
}
module.exports={validateScene,compileScene,renderScene,layoutSceneCallouts,shadedFill,mesh,rotate};
